import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';

import { Observable, catchError, map, of, switchMap, tap, timeout } from 'rxjs';

import { environment } from '../../environments/environment';
import {
  STATUS_PAGE_CONFIG_TIMEOUT_IN_MS,
  STATUS_PAGE_MAX_REFRESH_INTERVAL_IN_SECONDS,
  STATUS_PAGE_MIN_REFRESH_INTERVAL_IN_SECONDS,
  STATUS_PAGE_RUNTIME_CONFIG_URL,
  SYSTEM_HEALTH_DETAIL_PATH,
} from '../constants/status-page.constants';
import { StatusPageConfig } from '../types/status-page-config.types';

type ConfigParser<Key extends keyof StatusPageConfig> = (value: unknown) => StatusPageConfig[Key] | undefined;

/** One entry per key, so a new field cannot be added without a parser for it. */
type ConfigParsers = { [Key in keyof StatusPageConfig]: ConfigParser<Key> };

function parseText(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

const CONFIG_PARSERS: ConfigParsers = {
  gatewayUrl: value => parseText(value)?.replace(/\/$/, ''),
  logoSrc: parseText,
  title: parseText,
  company: parseText,
  refreshIntervalInSeconds: value =>
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= STATUS_PAGE_MIN_REFRESH_INTERVAL_IN_SECONDS &&
    value <= STATUS_PAGE_MAX_REFRESH_INTERVAL_IN_SECONDS
      ? value
      : undefined,
};

/**
 * Runtime configuration: build-time defaults merged with an optional JSON file
 * next to the bundle. A missing, malformed or stalled file is not an error — the
 * defaults are a complete configuration on their own, and a status page that
 * renders nothing at all is the worst available failure mode.
 */
@Injectable({ providedIn: 'root' })
export class StatusPageConfigService {
  private readonly http = inject(HttpClient);

  private readonly configSignal = signal<StatusPageConfig>(environment);
  private readonly isDetailViewSignal = signal(false);

  readonly config = this.configSignal.asReadonly();
  /**
   * Detail access is keyless and decided by the gateway (`SYSTEM_HEALTH_DETAIL_ENABLED`):
   * a 200 from the detail route at boot switches the page to the internal view, any
   * refusal or failure renders the public view. Nothing secret ever reaches the FE.
   */
  readonly isDetailView = this.isDetailViewSignal.asReadonly();

  /**
   * Loads the runtime configuration, then probes detail access once.
   *
   * @returns The effective configuration; the initializer blocks on it.
   */
  load(): Observable<StatusPageConfig> {
    return this.http.get<unknown>(STATUS_PAGE_RUNTIME_CONFIG_URL).pipe(
      timeout(STATUS_PAGE_CONFIG_TIMEOUT_IN_MS),
      map(override => ({ ...environment, ...this.parseConfig(override) })),
      catchError(() => of(environment)),
      tap(config => this.configSignal.set(config)),
      switchMap(config => this.probeDetailAccess(config))
    );
  }

  /** Only known keys with the right type and range survive — a typo must not break the page. */
  private parseConfig(override: unknown): Partial<StatusPageConfig> {
    const parsed: Partial<StatusPageConfig> = {};
    if (!override || typeof override !== 'object') return parsed;

    const source = override as Record<string, unknown>;
    for (const key of Object.keys(CONFIG_PARSERS) as (keyof StatusPageConfig)[]) {
      const parser = CONFIG_PARSERS[key] as ConfigParser<typeof key>;
      const value = parser(source[key]);
      if (value !== undefined) Object.assign(parsed, { [key]: value });
    }
    return parsed;
  }

  /**
   * A page booted during a full gateway outage lands on the public view and stays
   * there until reload — acceptable, because the public view is the safe shape and
   * the poller surfaces the outage either way.
   */
  private probeDetailAccess(config: StatusPageConfig): Observable<StatusPageConfig> {
    return this.http.get<unknown>(`${config.gatewayUrl}${SYSTEM_HEALTH_DETAIL_PATH}`).pipe(
      timeout(STATUS_PAGE_CONFIG_TIMEOUT_IN_MS),
      map(() => true),
      catchError(() => of(false)),
      tap(hasDetailAccess => this.isDetailViewSignal.set(hasDetailAccess)),
      map(() => config)
    );
  }
}
